import {Map, View} from "ol";
import {fromLonLat, get as getProjection, Projection, transform, transformExtent} from "ol/proj";
import {Attribution, defaults as defaultControls, OverviewMap, Rotate, ScaleLine} from "ol/control";
import {displayFeatureInfo} from "@/modules/ol/service/ol-service-wms";
import {deselectFeature, selectVectorFeature} from "@/modules/ol/service/ol-service-wfs";
import {isDrawActive} from "@/helpers/ol/draw/ol-draw-polygon-helper";
import {viewFit} from "@/helpers/ol/ol-view";
import TileLayer from "ol/layer/Tile";
import {OSM, XYZ} from "ol/source";
import {setMapClickMarker} from "@/helpers/ol/ol-map-click-layer-helper";
import {
    getBySelector,
    hideElementsBySelector,
    isVisibleBySelector,
    toggleHeaderBySelector
} from "@/helpers/element-helper";
import {blockUi, unblockUi} from "@/modules/elements-blockui";
import {getKeyIsRightMouseButton, loadRightMouseButton} from "@/helpers/right-mouse-button-helper";
import {isCesiumEnabled} from "@/modules/cesium/cesium";
import {Point} from "ol/geom";
import {isMoveLayerActive} from "@/helpers/ol/map/ol-move-helper";
import {getLocationByAddress, getLocationById, getLocationByLngLat} from "@/http/services/http-services-route";
import {getDefaultSession} from "@/helpers/user-session-helper";
import {MousePositionControl} from "@/helpers/ol/ol-mouse-helper";
import {printAddRectangle} from "@/helpers/print/print-scales-helper";
import {getBookmarkPresentations, getLayerByName} from "@/helpers/ol/ol-layer-helper";
import {setLayerByViewResolution} from "@/helpers/ol/view/ol-view-resolutation-helper";
import {defaults, DragRotate, DragRotateAndZoom} from "ol/interaction";
import {altKeyOnly, platformModifierKey, platformModifierKeyOnly} from "ol/events/condition";
import {getViewProjection, getViewProjectionName} from "@/helpers/ol/ol-projection-helper";
import {snapWmsLayer} from "@/helpers/ol/ol-snap-helper";
import {setGeoLocation} from "@/helpers/ol/ol-geolocation-helper";
import {replaceDefaultValueParams} from "@/helpers/datatable/datatable-editor-replace-param-helper.js";
import Swipe from "ol-ext/control/Swipe.js";
import {updateInfoLegends} from "@/helpers/layer/info/layer-info-legend-helper.js";

// const center = fromLonLat([  5.898002988235836, 51.48666508926052])
// const center = fromLonLat([  4.8022845613568705, 52.27744206188498])
const zoom = 13
let extent
let viewProjection
export function resetMap() {
    viewFit(extent)
}

/**
 * Init OpenLayers map
 * @param target
 * @returns {Map}
 */
export function initMap(target) {

    viewProjection = getViewProjection()
    const scaleLine = new ScaleLine({
        units: 'metric',
    });

    window.overviewMapControl = new OverviewMap({
        className: 'ol-overviewmap',
        layers: [
            new TileLayer({
                source: new OSM(),
            }),
        ],
    });

    const viewOptions = {
        projection: viewProjection,
        zoom: zoom,
        enableRotation: true
    }

    if(window.settings.minZoomLevel) {
        viewOptions.maxZoom = window.settings.minZoomLevel
    }
    if(window.settings.maxZoomLevel) {
        viewOptions.minZoom = window.settings.maxZoomLevel
    }
    if(window.settings.Restrict) {
        viewOptions.extent = getDefaultViewBbox()
    }

    if(window.settings.MapPinchZoom === 1) {
        viewOptions.constrainResolution = true
    }

    window.view = new View(viewOptions)
    const attribution = new Attribution({
        collapsible: false,
    });
    const mapOptions = {
        controls: defaultControls({
            attribution: false,
        }).extend([
            // new RotateNorthControl(this),
            scaleLine,
            new MousePositionControl(this),
            attribution,
            window.overviewMapControl
        ]),
        interactions: defaults({altShiftDragRotate: false}),
        target: target,
        layers: [],
        view: window.view
    }

    if(window.settings.Enable2DRotate) {
        mapOptions.interactions = mapOptions.interactions.extend([
            new DragRotate({condition: platformModifierKey})
        ])
    }

    window.map = new Map(mapOptions);

    setDefaultView()

    window.map.on('singleclick', function (evt) {
        const pixel = window.map.getEventPixel(evt.originalEvent);
        const coordinate = evt.coordinate;

        clickMap(pixel, coordinate)
    });

    window.map.on('pointermove', function (evt) {
        // focusMap()

        if(isDrawActive()) {
            return
        }

        selectVectorFeature(window.map, evt.pixel, 'hover')
    });

    map.getViewport().addEventListener('contextmenu', function (evt) {
        evt.preventDefault();
        const coordinate = map.getEventCoordinate(evt)

        toggleHeaderBySelector('bookmark-right-mouse-button', true)
        setMapClickMarker(coordinate)
        loadRightMouseButton(coordinate)
    })

    map.getView().on('change:rotation', (evt) => {
        // TODO: implement rotation for print
        // printAddRectangle()
    })

    let uploadLegends = true
    map.getView().on('change:resolution', function (evt) {
       setLayerByViewResolution(evt.target.getResolution())

        if(uploadLegends === true) {
            uploadLegends = false
            setTimeout(() => {
                updateInfoLegends()
                uploadLegends = true
            }, 1000)
        }
    });
    setLayerByViewResolution()

    focusMap()

    if(window.urlParamsObject.slider) {
        if(!window.ctrl) {
            window.ctrl = new Swipe();
        }
        map.addControl(window.ctrl);

        setTimeout(() => {
            const layers = window.urlParamsObject.slider.split(',')
            window.ctrl.addLayer(getLayerByName(layers[0]));

            getBySelector('#selectCompareLayerLeft').value = layers[0]
            getBySelector('#selectCompareLayerRight').value = layers[1]

            window.ctrl.addLayer(getLayerByName(layers[1]), true);
        }, 2000)
    }
}

export function focusMap() {
    getBySelector('#map').focus()
}

export function setDefaultView() {
    const userSession = getDefaultSession()

    if(window.urlParamsObject.x) {
       setDefaultViewUrlParamObject('x')
    } else if(window.urlParamsObject.adres) {
        setDefaultViewUrlParamObject('adres')
    } else if (userSession) {
        setDefaultViewUserSession(userSession)
    } else if(window.urlParamsObject.presentatie) {
        setDefaultViewUrlParamObject('presentation')
    } else {
        setDefaultViewBbox()
    }

    if(window.urlParamsObject.centreergps) {
        setGeoLocation(window.map)
    }
}

function setDefaultViewBbox() {
    const extent = getDefaultViewBbox()

    viewFit(extent, undefined, undefined, 0)
}

function getDefaultViewBbox() {

    if(window.settings.ViewProjection !== 'EPSG:28992') {
        return window.settings.BBOX.split(',').map(Number)
    }

    const bbox = window.settings.BBOX.split(',').map(Number)

    const centerX = bbox[0] + (bbox[2] - bbox[0]) / 2
    const centerY = bbox[1] + (bbox[3] - bbox[1]) / 2

    const centerRBX = bbox[2]

    let centerRBY
    if(bbox[1] > bbox[3]) {
        centerRBY = bbox[1]
    } else {
        return window.settings.BBOX.split(',').map(Number)
    }

    const centerRB = transform([centerRBX, centerRBY], "EPSG:28992", viewProjection)
    const center = transform([centerX, centerY], "EPSG:28992", viewProjection)

    extent = []
    extent[0] = center[0] - (centerRB[0] - center[0])
    extent[1] = center[1] - (centerRB[1] - center[1])
    extent[2] = center[0] + (centerRB[0] - center[0])
    extent[3] = center[1] + (centerRB[1] - center[1])

    console.log(extent)

    return extent


}

export function setDefaultViewUrlParamObject(object) {
    switch (object) {
        case 'x':
            if(window.urlParamsObject.x) {
                extent = transform([Number(window.urlParamsObject.x), Number(window.urlParamsObject.y)], getViewProjectionName(), getViewProjectionName())
                viewFit(new Point(extent), undefined, window.urlParamsObject.zoom, 0)

                setMapClickMarker(extent)
            }
            break;
        case 'adres':
            getLocationByAddress(window.urlParamsObject.adres).then(response => {
                return response.json()
            }).then(result => {

                const coordinates = result.split(' ')

                extent = transform([Number(coordinates[0]), Number(coordinates[1])], getViewProjectionName(), getViewProjectionName())
                setMapClickMarker(extent)

                let zoom = window.urlParamsObject.zoom
                if(!zoom) {
                    zoom = 18
                }
                viewFit( new Point(extent), undefined, zoom, 0)
            })
            break;
        case 'presentation':
            setDefaultViewBbox()
            break;
    }
}

function setDefaultViewUserSession(userSession) {
    extent = transform([Number(userSession.Y), Number(userSession.X)], 'EPSG:28992', getViewProjectionName())
    viewFit( new Point(extent), undefined, userSession.Zoom, 0)
}

export function clickMap(pixel, coordinate) {

    window.clickMapPixel = pixel
    window.clickMapCoordinate = coordinate

    if(isCesiumEnabled()) {
        return
    }

    if(isDrawActive()) {
        snapWmsLayer(coordinate, pixel)
        // console.log('snap when active')
        return
    }

    if(isMoveLayerActive()) {
        return;
    }

    console.log('testttt')

    if(isVisibleBySelector('bookmark-right-mouse-button') && getKeyIsRightMouseButton() === false) {

        if(window.rightMouseButtonValue) {

            setMapClickMarker(coordinate)
            getLocationByLngLat(coordinate[0], coordinate[1]).then((result) => {
                getLocationById(result.adres.id).then(location => {
                    const value = replaceDefaultValueParams(result, location.response.docs[0], window.rightMouseButtonValue, coordinate)
                    window.open(value, 'rightmouse')

                    delete window.rightMouseButtonValue
                })
            })


        } else {
            setMapClickMarker(coordinate)
            loadRightMouseButton(coordinate, false)
        }

        return
    }

    deselectFeature()
    const selected = selectVectorFeature(window.map, pixel, 'click', coordinate)

    console.log('click')
    console.log(selected)

    // Get WMS if there is no WFS clicked
    if(selected === false) {
        displayFeatureInfo(pixel,coordinate);
    }
}
